import type { CloudCostReport } from "@hasheeai/types";
import { useState } from "react";
import { PageHeader } from "../../components/PageHeader";
import { t } from "../../i18n";
import * as s from "../../styles/shared";
import { CostPie } from "./CloudCostCharts";
import { CostHelp } from "./CloudCostHelp";
import { CloudCostInvoices } from "./CloudCostInvoices";
import { DailyCostTable, FixedCostTable } from "./CloudCostTables";
import { useCloudCosts } from "./cloud-cost-data";
import {
  categoryTotals,
  costColors,
  monthOptions,
  utcDate,
} from "./cloud-cost-model";
export function CloudCostsPage(): React.JSX.Element {
  const today = new Date().toISOString().slice(0, 10);
  const [month, setMonth] = useState(today.slice(0, 7));
  const { report, loading, error, refresh } = useCloudCosts(month);
  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      <PageHeader
        title={t("cost.title")}
        actions={
          <button
            type="button"
            style={s.refreshBtn}
            disabled={loading}
            onClick={refresh}
          >
            {t("cost.refresh")}
          </button>
        }
      />
      <CompactControls
        today={today}
        month={month}
        setMonth={setMonth}
        report={report ?? undefined}
      />
      {error && (
        <p role="alert" style={s.errorBanner}>
          {error}
        </p>
      )}
      {loading && <output>{t("common.loading")}</output>}
      {report && <CompactReport report={report} today={today} />}
    </div>
  );
}

function CompactControls({
  today,
  month,
  setMonth,
  report,
}: {
  today: string;
  month: string;
  setMonth: (month: string) => void;
  report?: CloudCostReport;
}): React.JSX.Element {
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 16,
        alignItems: "center",
        margin: "16px 0",
      }}
    >
      <select
        aria-label={t("cost.history")}
        value={month}
        onChange={(event) => setMonth(event.target.value)}
      >
        {monthOptions(today).map((value) => (
          <option key={value} value={value}>
            {value}
          </option>
        ))}
      </select>
      {report && (
        <span style={s.cardLabel}>
          {t("cost.last_sync")}: {syncTime(report)} UTC
        </span>
      )}
      <SyncHelp report={report} />
      {report?.sources.some((source) => source.status !== "ok") && (
        <output>{t("cost.sync_failed_short")}</output>
      )}
    </div>
  );
}

function CompactReport({
  report,
  today,
}: { report: CloudCostReport; today: string }): React.JSX.Element {
  return (
    <>
      <FixedCostTable report={report} />
      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(min(100%, 320px), 1fr))",
          gap: 20,
          marginTop: 20,
          alignItems: "start",
        }}
      >
        <DailyCostTable key={report.month} report={report} today={today} />
        <CostPie
          title={t("cost.variable_composition")}
          slices={categoryTotals(report.lines).map(({ category, amount }) => ({
            name: t(`cost.category.${category}`),
            value: amount,
            color: costColors[category],
          }))}
        />
      </div>
      <CloudCostInvoices report={report} provider="all" />
    </>
  );
}

function SyncHelp({ report }: { report?: CloudCostReport }): React.JSX.Element {
  return (
    <CostHelp title={t("cost.last_sync")}>
      <p>{t("cost.auto_sync_help")}</p>
      {report?.sources.map((source) => (
        <p key={source.provider}>
          {source.provider === "neon" ? "Neon" : "Cloudflare"}:{" "}
          {source.synced_at ? utcDate(source.synced_at) : "—"} ·{" "}
          {t(`cost.${source.status}`)}
        </p>
      ))}
    </CostHelp>
  );
}

function syncTime(report: CloudCostReport): string {
  const times = report.sources
    .map((source) => source.synced_at)
    .filter((value): value is string => value !== null)
    .sort();
  return times.length ? utcDate(times[0]) : "—";
}
